import React from 'react';
import { Card } from '../SubscriptionAppCard';
import { Badge } from '../SubscriptionAppBadge';
import { HoverTooltip } from './HoverTooltip';
import { orderChannels } from '../../data/mockContactOrders';
import type { OrderChannel } from '../../data/mockContactOrders';
import type { ChannelSummary, OrderChannelFilter } from '../../utils/orderChannels';
import { channelBadgeVariant } from '../../utils/orderChannels';
interface OrdersChannelSummaryProps {
  summary: ChannelSummary;
  filter: OrderChannelFilter;
  onFilterChange: (filter: OrderChannelFilter) => void;
}
const formatCurrency = (value: number) => `$${value.toLocaleString('en-AU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})}`;

// Club vs one-time split for the contact's orders, plus the Orders tab channel filter
export function OrdersChannelSummary({
  summary,
  filter,
  onFilterChange
}: OrdersChannelSummaryProps) {
  const total = summary.clubCount + summary.oneTimeCount;
  const clubPercent = total > 0 ? Math.round(summary.clubCount / total * 100) : 0;
  const oneTimePercent = total > 0 ? 100 - clubPercent : 0;
  const describe = (channel: OrderChannel) => orderChannels.find(item => item.channel === channel)?.description ?? '';
  return <Card padding="none" data-id="element-1660" data-mp="afl14fph0pr3">
      <div className="p-6 bg-white rounded-lg" data-id="element-1661" data-mp="abnjxzmge89u">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1662" data-mp="5dvoivmlo40w">
          <div data-id="element-1663" data-mp="je6hq5bdtup8">
            <p className="text-xl font-semibold text-[#0F172A]" data-id="element-1664" data-mp="jdmzenhtl4ao">
              Club orders {summary.clubCount}
              <span className="text-[#CBD5E1] mx-2" aria-hidden="true" data-id="element-1665" data-mp="fcbkpd0hi3h9">·</span>
              One-time orders {summary.oneTimeCount}
            </p>
            <p className="text-sm text-[#64748B] mt-1" data-id="element-1666" data-mp="o5erx3kjdk3g">All orders for this contact</p>
          </div>
          <div className="flex items-center gap-2" data-id="element-1667" data-mp="132p30bt9onyn">
            <label htmlFor="orders-channel-filter" className="text-sm font-medium text-[#475569]" data-id="element-1668" data-mp="133gv3cnno3k6">
              Channel
            </label>
            <select id="orders-channel-filter" value={filter} onChange={e => onFilterChange(e.target.value as OrderChannelFilter)} className="h-[40px] pl-3 pr-8 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-1669" data-mp="156sq47u90cc8">
              <option value="all" data-id="element-1670" data-mp="1jvf5g9jvl43g">All orders</option>
              <option value="group:club" data-id="element-1671" data-mp="1jx9thwqg1cbr">Club orders</option>
              <option value="group:one-time" data-id="element-1672" data-mp="1ovjmc0op40u7">One-time orders</option>
              <optgroup label="Club channels" data-id="element-1673" data-mp="1swda8u251fn3">
                {orderChannels.filter(item => item.group === 'club').map(item => <option key={item.channel} value={item.channel} data-id="element-1674" data-mp="1xvpxhfwx8oq7">
                    {item.channel}
                  </option>)}
              </optgroup>
              <optgroup label="One-time channels" data-id="element-1675" data-mp="22u18vv2v77aj">
                {orderChannels.filter(item => item.group === 'one-time').map(item => <option key={item.channel} value={item.channel} data-id="element-1676" data-mp="3jrye6sbt981">
                    {item.channel}
                  </option>)}
              </optgroup>
            </select>
          </div>
        </div>

        <div className="mt-4 flex h-2 w-full overflow-hidden rounded-full bg-[#F1F5F9]" role="img" aria-label={`${clubPercent}% club orders, ${oneTimePercent}% one-time orders`} data-id="element-1677" data-mp="ytzmqhxvp29">
          <div className="h-full bg-[#3B82F6]" style={{
          width: `${clubPercent}%`
        }} data-id="element-1678" data-mp="3ncv5os6z8zkg" />
          <div className="h-full bg-[#94A3B8]" style={{
          width: `${oneTimePercent}%`
        }} data-id="element-1679" data-mp="3ifbqnq2ggirg" />
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-id="element-1680" data-mp="ewsb9o4vl7e2">
          <span className="flex items-center gap-2 text-[#475569]" data-id="element-1681" data-mp="euxoe4a1n57c">
            <span className="w-2 h-2 rounded-full bg-[#3B82F6]" aria-hidden="true" data-id="element-1682" data-mp="9w49c7f3z652" />
            {clubPercent}% club · <span className="font-medium text-[#0F172A]" data-id="element-1683" data-mp="4y2uxzsmnuzx">{formatCurrency(summary.clubSpend)}</span>
          </span>
          <span className="flex items-center gap-2 text-[#475569]" data-id="element-1684" data-mp="2ultokljq4tya">
            <span className="w-2 h-2 rounded-full bg-[#94A3B8]" aria-hidden="true" data-id="element-1685" data-mp="2po1xtuutfdvo" />
            {oneTimePercent}% one-time · <span className="font-medium text-[#0F172A]" data-id="element-1686" data-mp="icbp501ks829">{formatCurrency(summary.oneTimeSpend)}</span>
          </span>
        </div>

        <ul className="mt-4 pt-4 border-t border-[#E2E8F0] flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-1687" data-mp="8fgsynsryjr5">
          {summary.byChannel.map(item => <li key={item.channel} data-id="element-1688" data-mp="dfl9uexlhghw">
              <HoverTooltip content={describe(item.channel)} data-id="element-1689" data-mp="detgzbvdtwvb">
                <span className="flex items-center gap-1.5 cursor-default" data-id="element-1690" data-mp="1155tz15hijsr">
                  <Badge variant={channelBadgeVariant(item.channel)} data-id="element-1691" data-mp="163qvd3wh39is">{item.channel}</Badge>
                  <span className="text-sm font-semibold text-[#0F172A]" data-id="element-1692" data-mp="r66ua82p5yhw">{item.count}</span>
                </span>
              </HoverTooltip>
            </li>)}
        </ul>
      </div>
    </Card>;
}